// Pure metric helpers for the Hub Dashboard. Extracted from the page component
// so the number-crunching (variance, DOW factor, package-mix split, actual
// rollups) is unit-tested and can't silently regress when the dashboard grows.
//
// MAPE itself lives in engine.ts (meanAbsPctError) — reuse it, don't duplicate.

/** Variance of actual vs forecast, as a percent. Null when there's nothing to compare. */
export function variancePct(forecast: number, actual: number | null): number | null {
  return forecast > 0 && actual != null ? ((actual - forecast) / forecast) * 100 : null;
}

/** Day-of-week factor: a day's forecast relative to the week's average day (×1 = average). */
export function dowFactor(dayForecast: number, weekTotalForecast: number): number {
  return weekTotalForecast > 0 ? dayForecast / (weekTotalForecast / 7) : 0;
}

/** Lane package-mix percentages (0..100). */
export interface PackageMix {
  cartons: number;
  polybags: number;
  irregular: number;
}

/** A volume split into package types; `unspec` holds volume with no usable mix. */
export interface PackageSplit {
  cartons: number;
  polybags: number;
  irregular: number;
  unspec: number;
}

export function emptyPackageSplit(): PackageSplit {
  return { cartons: 0, polybags: 0, irregular: 0, unspec: 0 };
}

/**
 * Split a forecast volume by a lane's package mix. If the mix has any positive
 * share, split proportionally; otherwise the whole volume is "unspecified".
 */
export function splitPackageMix(forecast: number, mix: PackageMix | null | undefined): PackageSplit {
  if (forecast <= 0) return emptyPackageSplit();
  const sum = mix ? mix.cartons + mix.polybags + mix.irregular : 0;
  if (mix && sum > 0) {
    return {
      cartons: (forecast * mix.cartons) / 100,
      polybags: (forecast * mix.polybags) / 100,
      irregular: (forecast * mix.irregular) / 100,
      unspec: 0,
    };
  }
  return { cartons: 0, polybags: 0, irregular: 0, unspec: forecast };
}

/** Add two package splits (for accumulating across customers/lanes/days). */
export function addPackageSplit(a: PackageSplit, b: PackageSplit): PackageSplit {
  return {
    cartons: a.cartons + b.cartons,
    polybags: a.polybags + b.polybags,
    irregular: a.irregular + b.irregular,
    unspec: a.unspec + b.unspec,
  };
}

/** Total of a package split. */
export function packageSplitTotal(s: PackageSplit): number {
  return s.cartons + s.polybags + s.irregular + s.unspec;
}

/** Sum imported actuals over a set of dates. `hasAct` is false when none were imported. */
export function actualForDates(
  actByDate: Map<string, number>,
  dates: string[]
): { total: number; hasAct: boolean } {
  let total = 0;
  let hasAct = false;
  for (const d of dates) {
    if (actByDate.has(d)) {
      total += actByDate.get(d) ?? 0;
      hasAct = true;
    }
  }
  return { total, hasAct };
}
